<template>
  <div>
    <v-card-title class="align-end">
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Search"
        single-line
        hide-details
      ></v-text-field>
      <v-spacer></v-spacer>
      <div tooltip="Voir les propositions abandonnées">
        <v-icon
          class="mr-3"
          :color="showDisabled ? 'primary' : 'grey'"
          @click="showDisabled = !showDisabled"
          >visibility</v-icon
        >
      </div>

      <v-menu offset-y :close-on-content-click="false" left v-if="editMode">
        <template v-slot:activator="{ on }">
          <v-icon class="mr-3" color="primary" v-on="on">add_box</v-icon>
        </template>
        <v-card style="width: 300px" class="px-2 py-2">
          <v-layout row align-baseline justify-space-between>
            <v-flex xs8>
              <v-text-field
                v-model="hostId"
                label="Identifiant du bien"
              ></v-text-field>
            </v-flex>
            <v-flex xs4>
              <v-btn flat color="primary" @click="createProposal()"
                >Ajouter</v-btn
              >
            </v-flex>
          </v-layout>
        </v-card>
      </v-menu>
      <v-menu offset-y left>
        <template v-slot:activator="{ on }">
          <v-icon color="#FF7C5D" v-on="on">picture_as_pdf</v-icon>
        </template>
        <v-list>
          <v-list-tile>
            <v-list-tile-title @click="exportAll"
              >Tous les biens</v-list-tile-title
            >
          </v-list-tile>
          <v-list-tile>
            <v-list-tile-title @click="exportLiked"
              >Biens aimés seulement</v-list-tile-title
            >
          </v-list-tile>
        </v-list>
      </v-menu>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="proposals"
      :custom-sort="customSort"
      class="proposals-table"
      hide-actions
    >
      <template v-slot:items="props">
        <ProjectPropositionsRow
          :item="props.item"
          mode="proposal"
          v-model="props.expanded"
        />
      </template>
      <template v-slot:expand="props">
        <ProjectProposition :item="props.item" :edit-mode="editMode" />
      </template>
    </v-data-table>
    <v-snackbar v-model="showError" :timeout="7000"
      >Une erreur a eu lieu</v-snackbar
    >
    <v-snackbar v-model="showDuplicateError" :timeout="7000"
      >Cette proposition existe déjà</v-snackbar
    >
  </div>
</template>

<script>
import ProjectPropositionsRow from "./ProjectPropositionsRow";
import { exportPDFProposals, fetchHil, sortPositions } from "../../utils";
import { PROJECT_STATUS } from "../../data/DefaultSettings";
import ProjectProposition from "./ProjectProposition";
import { addDistancesToProposal } from "../../services/maps";
import { getObjectValueByPath } from "vuetify/lib/util/helpers";
import { mapActions, mapState } from "vuex";

export default {
  components: {
    ProjectProposition,
    ProjectPropositionsRow
  },
  props: ["project", "editMode"],
  data() {
    return {
      search: "",
      hostId: "",
      showError: false,
      showDuplicateError: false,
      showDisabled: false
    };
  },
  async beforeMount() {
    await this.refreshProposals();
  },
  methods: {
    ...mapActions("projects", ["fetchProposals"]),

    async refreshProposals() {
      const id = this.project._id;
      await this.fetchProposals({ id });
    },

    customSort(items, index, isDesc) {
      const chosen = this.project.chosenEstate;
      if (index === "position") {
        items.sort(sortPositions(chosen, index, isDesc));
      } else if (index === "visited.dateVisite") {
        items.sort((a, b) => {
          if (!isDesc) {
            return a.visited?.dateVisite < b.visited?.dateVisite ? -1 : 1;
          } else {
            return b.visited?.dateVisite < a.visited?.dateVisite ? -1 : 1;
          }
        });
      } else if (index === "propvisite") {
        items.sort((a, b) => {
          if (!isDesc) {
            return a.visited && !b.visited ? -1 : 1;
          } else {
            return b.visited && !a.visited ? -1 : 1;
          }
        });
      } else if (index === "status") {
        items.sort((a, b) => {
          const [readable1] = PROJECT_STATUS.filter(
            ({ value }) => value === a.status
          );
          const [readable2] = PROJECT_STATUS.filter(
            ({ value }) => value === b.status
          );
          if (!isDesc) {
            return (readable1?.value || "") > (readable2?.value || "") ? -1 : 1;
          } else {
            return (readable1?.value || "") < (readable2?.value || "") ? -1 : 1;
          }
        });
      } else if (index != null) {
        // copypasta from vuetify (not overridable :/)
        return items.sort((a, b) => {
          let sortA = getObjectValueByPath(a, index);
          let sortB = getObjectValueByPath(b, index);

          if (isDesc) {
            [sortA, sortB] = [sortB, sortA];
          }

          // Check if both are numbers
          if (!isNaN(sortA) && !isNaN(sortB)) {
            return sortA - sortB;
          }

          // Check if both cannot be evaluated
          if (sortA === null && sortB === null) {
            return 0;
          }

          [sortA, sortB] = [sortA, sortB].map(s =>
            (s || "").toString().toLocaleLowerCase()
          );

          if (sortA > sortB) return 1;
          if (sortA < sortB) return -1;

          return 0;
        });
      }

      return items;
    },
    async createProposal() {
      try {
        const id = this.project._id;
        try {
          const resp = await fetchHil(`/hosts/${this.hostId}`);
          if (!resp.ok) {
            alert("Ce bien n'existe pas");
            return;
          }
        } catch (e) {
          alert("Ce bien n'existe pas");
          return;
        }
        const resp = await fetchHil(
          `/proposals/lease/${id}/host/${this.hostId}`,
          {
            method: "POST"
          }
        );
        if (!resp.ok) {
          if (resp.status === 409) {
            this.showDuplicateError = true;
          } else {
            this.showError = true;
          }
        }
        await this.refreshProposals();
        const host = await (await fetchHil(`/hosts/${this.hostId}`)).json();
        this.hostId = "";
        await addDistancesToProposal(this.project, host);
        await this.refreshProposals();
      } catch (err) {
        console.error(err);
        this.showError = true;
      }
    },
    async exportAll() {
      await exportPDFProposals(this.proposals, this.project);
    },
    async exportLiked() {
      await exportPDFProposals(
        this.proposals.filter(({ liked }) => liked != null),
        this.project
      );
    }
  },
  computed: {
    ...mapState("projects", {
      allProposals: state => state.proposals,
      proposalsLoaded: state => state.proposalsLoaded
    }),
    ...mapState("user", ["role"]),

    proposals() {
      let proposals;
      if (this.search !== "") {
        proposals = this.allProposals.filter(({ _host }) => {
          return (
            _host != null &&
            (_host.address.includes(this.search) ||
              (_host.contactName && _host.contactName.includes(this.search)) ||
              _host._id === this.search)
          );
        });
      } else {
        proposals = this.allProposals;
      }

      return proposals
        .filter(({ disabled }) => this.showDisabled || !disabled)
        .map(prop => {
          const { liked, disliked } = prop;
          if (prop.position == null) {
            prop.position = liked != null ? 99 : disliked != null ? 9999 : 999;
          }
          prop.id = prop._id;
          return prop;
        });
    },
    headers() {
      const h = [
        {
          text: "",
          align: "left",
          sortable: true,
          value: "position",
          width: "10",
          mobile: true
        },
        {
          text: "Créé",
          align: "left",
          sortable: true,
          value: "proposed",
          width: "10"
        },
        {
          text: "Adresse",
          align: "left",
          sortable: true,
          value: "_host.address",
          mobile: true
        },
        {
          text: "Surface",
          align: "left",
          sortable: true,
          value: "_host.carrez",
          width: "10"
        },
        {
          text: "Prix",
          align: "left",
          sortable: true,
          value: "_host.price",
          width: "10"
        },
        {
          text: "Pièces",
          align: "left",
          sortable: true,
          value: "_host.numberOfRoom",
          width: "10"
        },
        {
          text: "Nom",
          align: "left",
          sortable: true,
          value: "_host.contactName",
          width: "100"
        },
        {
          text: "Tel",
          align: "left",
          sortable: true,
          value: "_host.contactPhoneNumber",
          width: "10"
        },
        {
          text: "Voiture",
          align: "left",
          sortable: true,
          value: "distanceCar",
          width: "10"
        },
        {
          text: "Prop. visite",
          align: "left",
          sortable: true,
          value: "propvisite",
          width: "10"
        },
        {
          text: "Status",
          align: "left",
          sortable: true,
          value: "status",
          width: "10",
          mobile: true
        },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "",
          width: "10",
          mobile: true
        },
        {
          text: "",
          align: "left",
          sortable: false,
          value: "",
          width: "10",
          mobile: true
        }
      ];
      return this.$mq === "lg" ? h : h.filter(col => col.mobile);
    }
  }
};
</script>

<style>
.proposals-table {
  max-width: 95vw;
}
</style>
